<div class="container-fluid p-0 profile-page" data-bs-theme="dark">

    <!-- HEADER -->
    <HeaderSection
        :breadcrumb="breadcrumb"
        @breadcrumb-click="handleBreadcrumbClick"
        @open-assistant="showAssistant = true"
    />

    <!-- LOADING STATE -->
    <div v-if="loading" class="container text-center py-5">
        <div class="spinner-border text-warning" role="status">
            <span class="visually-hidden">Loading...</span>
        </div>
        <p class="text-body-secondary mt-2">Loading profile...</p>
    </div>

    <!-- ERROR STATE -->
    <div v-else-if="error" class="container py-5">
        <div class="alert alert-danger d-flex align-items-center gap-2 justify-content-center">
            <i class="bi bi-exclamation-triangle-fill"></i> {{ error }}
        </div>
    </div>

    <!-- PROFILE CONTENT -->
    <section v-else class="container profile-sections py-4">

        <!-- HERO -->
        <div class="card pv-card border-0 overflow-hidden mb-4">
            <div class="pv-cover"></div>

            <div class="card-body px-4 pb-4 pt-0">
                <div class="d-flex flex-column flex-md-row align-items-center align-items-md-end gap-3 gap-md-4 text-center text-md-start">
                    <div class="pv-avatar text-bg-warning rounded-4 d-flex align-items-center justify-content-center flex-shrink-0">
                        {{ avatar_initials }}
                    </div>

                    <div class="flex-grow-1 pb-md-1">
                        <h2 class="fw-bold text-white mb-2">{{ fullName }}</h2>
                        <div class="d-flex flex-wrap gap-2 justify-content-center justify-content-md-start">
                            <span class="badge rounded-pill bg-warning-subtle text-warning-emphasis border border-warning-subtle px-3 py-2">
                                <i class="bi bi-person-badge me-1"></i>{{ userRole }}
                            </span>
                            <span class="badge rounded-pill text-bg-dark border border-secondary-subtle px-3 py-2 fw-medium">
                                <i class="bi bi-calendar3 me-1"></i>Member since {{ joinYear }}
                            </span>
                            <span v-if="isEmailVerified" class="badge rounded-pill bg-success-subtle text-success-emphasis border border-success-subtle px-3 py-2 fw-medium">
                                <i class="bi bi-patch-check-fill me-1"></i>Verified
                            </span>
                        </div>
                    </div>

                    <router-link
                        to="/edit_profile"
                        class="btn btn-warning fw-semibold btn-sm rounded-3 text-xs d-inline-flex align-items-center justify-content-center gap-1"
                    >
                        <i class="bi bi-pencil-square"></i> Edit Profile
                    </router-link>
                </div>
            </div>
        </div>

        <div class="row g-4">

            <!-- PERSONAL INFORMATION -->
            <div class="col-lg-7">
                <div class="card pv-card border-0 h-100">
                    <div class="card-header bg-transparent border-bottom border-secondary-subtle px-4 py-3 d-flex align-items-center justify-content-between">
                        <h6 class="mb-0 fw-semibold text-white">
                            <i class="bi bi-person-lines-fill text-warning me-2"></i>Personal Information
                        </h6>
                    </div>

                    <ul class="list-group list-group-flush">
                        <li v-for="field in infoFields" :key="field.label" class="list-group-item bg-transparent px-4 py-3 d-flex align-items-center gap-3">
                            <span class="p-2 bg-warning-subtle text-warning border border-warning-subtle rounded-3 d-flex align-items-center justify-content-center flex-shrink-0">
                                <i class="bi" :class="field.icon"></i>
                            </span>
                            <div class="flex-grow-1 min-w-0">
                                <small class="d-block text-body-secondary text-uppercase fw-semibold">{{ field.label }}</small>
                                <span v-if="field.value" class="text-white text-break">{{ field.value }}</span>
                                <span v-else class="text-body-tertiary fst-italic">Not provided</span>
                            </div>
                            <router-link v-if="!field.value" to="/edit_profile" class="btn btn-outline-warning btn-sm rounded-3 text-xs d-inline-flex align-items-center justify-content-center gap-1">
                                <i class="bi bi-plus-lg"></i>Add
                            </router-link>
                        </li>
                    </ul>
                </div>
            </div>

            <!-- PROFILE STRENGTH -->
            <div class="col-lg-5">
                <div class="card pv-card border-0 h-100">
                    <div class="card-header bg-transparent border-bottom border-secondary-subtle px-4 py-3">
                        <h6 class="mb-0 fw-semibold text-white">
                            <i class="bi bi-graph-up-arrow text-warning me-2"></i>Profile Strength
                        </h6>
                    </div>

                    <div class="card-body px-4">
                        <div class="d-flex align-items-center gap-4 mb-4">
                            <div class="pv-ring rounded-circle d-flex align-items-center justify-content-center flex-shrink-0" :style="{ '--pv-progress': profileProgress }">
                                <div class="pv-ring-inner rounded-circle d-flex flex-column align-items-center justify-content-center">
                                    <span class="fs-4 fw-bold text-white lh-1">{{ profileProgress }}%</span>
                                </div>
                            </div>
                            <div>
                                <p class="fw-semibold text-white mb-1">
                                    {{ profileProgress === 100 ? 'All done!' : 'Almost there' }}
                                </p>
                                <small class="text-body-secondary">
                                    <template v-if="remainingItems > 0">
                                        Complete <strong class="text-white">{{ remainingItems }}</strong> more item{{ remainingItems !== 1 ? 's' : '' }} to reach 100%.
                                    </template>
                                    <template v-else>Your profile is complete.</template>
                                </small>
                            </div>
                        </div>

                        <div class="list-group gap-2">
                            <component
                                :is="item.done ? 'div' : 'router-link'"
                                v-for="item in completionItems"
                                :key="item.label"
                                v-bind="item.done ? {} : { to: '/edit_profile' }"
                                class="list-group-item rounded-3 border px-3 py-2 d-flex align-items-center gap-2 text-decoration-none"
                                :class="item.done
                                    ? 'bg-success-subtle border-success-subtle text-success-emphasis'
                                    : 'list-group-item-action list-group-item-warning border-warning-subtle'"
                            >
                                <i class="bi" :class="item.done ? 'bi-check-circle-fill text-success' : 'bi-circle text-warning'"></i>
                                <span class="flex-grow-1 small fw-medium">{{ item.label }}</span>
                                <i v-if="!item.done" class="bi bi-arrow-right-short fs-5 text-warning"></i>
                            </component>
                        </div>
                    </div>
                </div>
            </div>

        </div>
    </section>

    <!-- 🤖 VSI Assistant -->
    <BolideAssistant :is_open="showAssistant" @close-assistant="showAssistant = false" />

</div>
